import React, {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import * as CONSTANTS from 'Utils/constants';
import { ReactComponent as LeftArrowIcon } from 'Assets/svg/left-arrow-thicker.svg';
import { ReactComponent as RightArrowIcon } from 'Assets/svg/gradient-arrow.svg';
import { ReactComponent as TripleDotNavIcon } from 'Assets/svg/triple-dot-nav.svg';
import { ReactComponent as EmptySearch } from 'Assets/svg/empty-icon.svg';
import { ReactComponent as SearchIcon } from 'Assets/svg/search-icon.svg';
import { ReactComponent as InfoIcon } from 'Assets/svg/info-icon.svg';
import { ReactComponent as NewContactsImage } from 'Assets/svg/new-contacts-icon.svg';
import Input from 'Components/common/newInputField';
import Dropdown from 'Components/common/dropdown';
import { confirmationPopupState } from 'Components/common/confirmationPopup';
import { LoadingWrapper } from 'Components/common/loadingWrapper';
import Pagination from 'Components/common/pagination';
import InfoDropDownWithImg from 'Components/common/InfoDropDownWithImg';
import SharedListIcon from 'Components/common/SharedListIcon';
import { userDetail } from 'Stores/userDetail';
import { advSearchState } from 'Stores/advSearch';
import { Utils } from 'Utils/utils';
import { PAGE_PATH_MAP } from 'Utils/constants';
import './styles.scss';
import SavedSearchPopUp from './savedSearchPopUp';

let elementObserver = null;

const filtersLabelWithKeys = {
  location: {
    label: 'Location',
    keys: [
      'selectedCities',
      'selectedStates',
      'selectedCountries',
      'selectedRegions',
    ],
    objectKey: 'name',
  },
  industry: {
    label: 'Industry',
    keys: ['industrySectorsV2', 'excludeIndustrySectorsV2'],
    objectKey: 'name',
  },
  keyword: {
    label: 'Keyword',
    keys: ['companyKeywordV2', 'excludeCompanyKeywordV2'],
  },
  dept: {
    label: 'Department',
    keys: ['dept'],
  },
  title: {
    label: 'Title',
    keys: ['title', 'excludeTitle'],
  },
  level: {
    label: 'Level',
    keys: ['level'],
  },
  name: {
    label: 'Contact Name',
    keys: ['name'],
  },
  companyName: {
    label: 'Company Name',
    keys: ['companyName'],
  },
  employeeCount: {
    label: 'Employee Count',
    keys: ['employeeCount'],
  },
  revenue: {
    label: 'Revenue',
    keys: ['revenue'],
  },
  excludedDomains: {
    label: 'Excluded Domains',
    keys: ['excludedDomains'],
    objectKey: 'fname',
  },
  includedDomains: {
    label: 'Included Domains',
    keys: ['includedDomains'],
    objectKey: 'fname',
  },
  technology: {
    label: 'Technology',
    keys: ['technologies'],
  },
};

function getParsedData({ keys, value, parsedFilter }) {
  const availableKey = keys.find(key => parsedFilter[key] !== undefined);
  let data = [];
  if (availableKey)
    if (Array.isArray(parsedFilter[availableKey])) {
      if (typeof parsedFilter[availableKey][0] === 'object' && value.objectKey)
        if (keys.length > 1) {
          data = keys.reduce((result, key) => {
            if (parsedFilter[key] !== undefined)
              return [
                ...result,
                parsedFilter[key].map(prop => {
                  return prop[value.objectKey];
                }),
              ];
            return result;
          }, []);
        } else {
          data = parsedFilter[keys[0]].map(prop => {
            return prop[value.objectKey];
          });
        }
      else data = parsedFilter[keys[0]];
    } else if (
      typeof parsedFilter[availableKey] === 'object' &&
      value.objectKey
    ) {
      data = [parsedFilter[availableKey][value.objectKey]];
    }

  return data;
}

function getAppliedFilters(filterString) {
  const parsedFilter = JSON.parse(filterString);

  if (filterString !== null) {
    const appliedFilters = Object.entries(filtersLabelWithKeys).reduce(
      (output, entry) => {
        const [key, value] = entry;
        const includedKeys = value.keys.filter(
          text => !text.includes('exclude') && !text.includes('exclusion'),
        );
        const excludedKeys = value.keys.filter(
          text => text.includes('exclude') || text.includes('exclusion'),
        );

        if (
          includedKeys.some(
            includedKey =>
              parsedFilter[includedKey] !== undefined &&
              (Array.isArray(parsedFilter[includedKey])
                ? parsedFilter[includedKey].length > 0
                : true),
          ) ||
          excludedKeys.some(
            excludedKey =>
              parsedFilter[excludedKey] !== undefined &&
              (Array.isArray(parsedFilter[excludedKey])
                ? parsedFilter[excludedKey].length > 0
                : true),
          )
        )
          return [
            ...output,
            {
              key,
              text: value.label,
              included: getParsedData({
                keys: includedKeys,
                parsedFilter,
                value,
              }),
              excluded: getParsedData({
                keys: excludedKeys,
                parsedFilter,
                value,
              }),
            },
          ];
        return output;
      },
      [],
    );
    return appliedFilters;
  }
  return [];
}

function returnCapitalizedWord(word = '') {
  return word.charAt(0) + word.slice(1).toLowerCase();
}

const RenderAppliedFilters = props => {
  const { savedSearch } = props;
  const { searchTemplate, id } = savedSearch;
  const [showMoreData, setShowMoreData] = useState({
    show: false,
    expanded: false,
  });

  return (
    <div
      className={`wrapper${showMoreData.expanded ? ' show-all-data' : ''}`}
      ref={useCallback(node => {
        if (node && node.scrollHeight > node.clientHeight)
          setShowMoreData({ ...showMoreData, show: true });
      }, [])}
    >
      {getAppliedFilters(searchTemplate).map(appliedFilter => {
        return (
          <div className="filter-value" key={appliedFilter.key}>
            <span className="label">{`${appliedFilter.text}:`}</span>
            {appliedFilter.included.length > 0 &&
              appliedFilter.included.map(includedValue => {
                return (
                  <span className="included" key={`${id}-${includedValue}`}>
                    {includedValue}
                  </span>
                );
              })}
            {appliedFilter.excluded.length > 0 &&
              appliedFilter.excluded.map(excludedValue => {
                return (
                  <span className="excluded" key={`${id}-${excludedValue}`}>
                    {excludedValue}
                  </span>
                );
              })}
          </div>
        );
      })}
      {showMoreData.show && (
        <div
          className={`show-more-text${showMoreData.expanded ? ' expand' : ''}`}
          onClick={() =>
            setShowMoreData({
              ...showMoreData,
              expanded: !showMoreData.expanded,
            })
          }
        />
      )}
    </div>
  );
};

function reducer(state, action) {
  const { type, id, count } = action;
  switch (type) {
    case 'SET_COUNT_ID':
      return { ...state, countIds: { ...state.countIds, [id]: count } };
    case 'SET_LOADING_ID':
      return !state.loadingIds.includes(id) &&
        !Object.keys(state.countIds).includes(id)
        ? {
            ...state,
            loadingIds: [...state.loadingIds, id],
            recentLoadingId: id,
          }
        : state;
    case 'REMOVE_LOADING_ID':
      return {
        ...state,
        loadingIds: state.loadingIds.filter(loadingId => loadingId !== id),
      };
    default:
      return state;
  }
}

export const EmptySavedSearches = ({
  searchTerm,
  noSavedSearch,
  emptySavedSearch,
}) => {
  return (
    <>
      {noSavedSearch && (
        <div className="save-searches-no-results">
          <SearchIcon />
          <div className="no-data-text">
            You have not saved any searches. Start saving your search criteria
            and retrieve search results easily without having to enter all
            filters each time.
          </div>
          <div className="nolist-action-wrap">
            <Link to={CONSTANTS.PAGE_PATH_MAP.CONTACT_SEARCH}>
              <div className="contact-search-button">Go to Contact Search</div>
            </Link>
          </div>
        </div>
      )}
      {emptySavedSearch && (
        <div className="save-searches-no-results">
          <EmptySearch />
          <div className="no-data-text">
            No result found for the saved search, <br />
            <b>{`“${searchTerm}”`}</b>
          </div>
        </div>
      )}
    </>
  );
};

const SavedSearch = observer(props => {
  const { history } = props;
  const [searchTerm, setSearchTerm] = useState('');
  const [showLoader, setShowLoader] = useState(false);
  const [isUpdateAPILoading, setIsUpdateAPIloading] = useState(false);
  const [popupData, setPopupData] = useState({ show: false, data: {} });
  const [newContactsCountData, dispatch] = useReducer(reducer, {
    loadingIds: [],
    countIds: {},
    recentLoadingId: '',
  });
  const [pagination, setPagination] = useState({ page: 1, count: 10 });
  const tableBodyRef = useRef();

  const searchRegex = new RegExp(searchTerm, 'gi');

  const searchedResults = userDetail.contactSavedSearches.filter(
    savedSearch => {
      const { templateName } = savedSearch;
      return (templateName || '').match(searchRegex);
    },
  );

  const savedSearchesToShow = searchedResults.filter(
    (...args) =>
      args[1] >= (pagination.page - 1) * pagination.count &&
      args[1] < pagination.page * pagination.count,
  );

  const changePageLimit = val => {
    setPagination({ count: val, page: 1 });
  };

  const changePage = pageNo => {
    setPagination({ ...pagination, page: pageNo + 1 });
  };

  const updateLoadingId = id => {
    dispatch({
      type: 'SET_LOADING_ID',
      id,
    });
  };

  const updateNewCountData = async id => {
    const callback = () => {
      dispatch({
        type: 'REMOVE_LOADING_ID',
        id,
      });
    };
    const count = await advSearchState.getSavedSearchesNewContactsCount({
      callback,
      id,
    });
    dispatch({
      type: 'SET_COUNT_ID',
      id,
      count,
    });
  };

  const updateSavedSearch = async data => {
    const { id, searchTemplate } = popupData.data;
    const { templateName, isSharedSearch, ...remainingProps } = data;
    const isEmailScheduleUpdated = Object.keys(remainingProps).some(
      key => remainingProps[key] !== popupData.data[key],
    );
    setIsUpdateAPIloading(true);
    const isSuccess = await advSearchState.updateSavedSearch({
      id,
      searchTemplate,
      templateName,
      isSharedSearch,
      ...(isEmailScheduleUpdated
        ? { ...remainingProps }
        : remainingProps.frequency
        ? {}
        : {
            status: 'INACTIVE',
          }),
    });
    if (isSuccess) {
      Utils.mixpanelTrack(CONSTANTS.MX_SAVED_SEARCH_EVENTS.EDIT_SAVED_SEARCH, {
        name: templateName,
        ...remainingProps,
      });
      setPopupData({ show: false, data: {} });
    }
    setIsUpdateAPIloading(false);
  };

  const updateSharing = async (data, eventName) => {
    setIsUpdateAPIloading(true);
    const { id, searchTemplate, templateName, isSharedSearch } = data;
    const isSuccess = await advSearchState.updateSavedSearch(
      {
        id,
        searchTemplate,
        templateName,
        isSharedSearch,
      },
      isSharedSearch
        ? {
            success: 'Search shared successfully! Ready to explore.',
            failure: 'Oops! Sharing failed. Please try again.',
          }
        : {
            success: 'Search unshared successfully. Your privacy is secured.',
            failure: 'Uh-oh! Unsharing failed. Please try again.',
          },
    );
    if (isSuccess)
      Utils.mixpanelTrack(eventName, {
        data,
      });
    setIsUpdateAPIloading(false);
  };

  const shareSavedSearch = async data => {
    updateSharing(data, CONSTANTS.MX_SAVED_SEARCH_EVENTS.SHARE_SAVED_SEARCH);
  };

  const unShareSavedSearch = async data => {
    updateSharing(data, CONSTANTS.MX_SAVED_SEARCH_EVENTS.UNSHARE_SAVED_SEARCH);
  };

  const deleteSavedSearch = savedSearch => {
    confirmationPopupState.setPopupValues({
      title: 'Delete Saved Search',
      content: `Are you sure you want to delete <b>“${savedSearch.templateName}”</b>?`,
      actionBtnText: 'Delete',
      customClassName: 'new-delete-popup',
      callback: () => {
        setShowLoader(true);
        advSearchState.deleteSavedSearch(savedSearch.id, () => {
          setShowLoader(false);
        });
      },
    });
    confirmationPopupState.setShowPopup(true);
    confirmationPopupState.setCustomStyling({ commonPopupMaskBG: '#0c194ba5' });
  };

  useEffect(() => {
    if (tableBodyRef.current) tableBodyRef.current.scrollTop = 0;
  }, [pagination.page, pagination.count, tableBodyRef]);

  useEffect(() => {
    if (newContactsCountData.recentLoadingId)
      updateNewCountData(newContactsCountData.recentLoadingId);
  }, [newContactsCountData.recentLoadingId]);

  useEffect(() => {
    elementObserver = new IntersectionObserver(function(entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const savedSearchId = entry.target.getAttribute('data-id');
          updateLoadingId(savedSearchId);
        }
      });
    });
    if (elementObserver) {
      const elements = document.querySelectorAll('.table-row');
      elements.forEach(element => {
        elementObserver.observe(element);
      });
    }
    Utils.mixpanelTrack(
      CONSTANTS.MX_SAVED_SEARCH_EVENTS.SAVED_SEARCH_PAGE_LOAD,
      { count: userDetail.contactSavedSearches.length },
    );
  }, []);

  return (
    <div
      className={`saved-search-wrapper${
        Utils.isSavedSearchUpdateEnabled() ? '' : ' update-disabled'
      } ${
        userDetail.isContactUpdatesEnabled ? '' : 'contact-updates-disabled'
      }`}
    >
      {popupData.show && (
        <SavedSearchPopUp
          onClose={() => setPopupData({ show: false, data: {} })}
          isEdit
          isAPILoading={isUpdateAPILoading}
          data={popupData.data}
          onSubmit={updateSavedSearch}
        />
      )}
      <LoadingWrapper
        className="saved-search-page-loader"
        show={showLoader || !userDetail.initialLoadedAPIs.contactSavedSearches}
        position="fixed"
      />
      <div className="top-wrapper">
        <Link to={PAGE_PATH_MAP.CONTACT_SEARCH}>
          <LeftArrowIcon />
        </Link>
        <h1>Saved Searches</h1>
        <div className="input-icon-wrap">
          <Input
            clearClick={() => setSearchTerm('')}
            showClearIcon
            showSearchIcon
            inputProps={{
              disabled: userDetail.contactSavedSearches.length === 0,
              value: searchTerm,
              placeholder: 'Search by saved name',
              onChange: value => {
                if (pagination.page > 1)
                  setPagination({ ...pagination, page: 1 });
                setSearchTerm(value);
              },
            }}
          />
        </div>
      </div>
      <div className="table-wrapper">
        <div className="table-header">
          <span>Name</span>
          <span className="creator">Creator</span>
          <span className="created-on">Created On</span>
          <span>Filters Applied</span>
          {Utils.isSavedSearchUpdateEnabled() && (
            <span className="email-schedule">Email Scheduled</span>
          )}
          {userDetail.isContactUpdatesEnabled && (
            <span className="new-contacts">
              New Contacts
              <div className="info-wrapper">
                <InfoIcon className="info-icon" />
                <InfoDropDownWithImg
                  title="New contacts added"
                  imageComponent={NewContactsImage}
                  wrapperClassName="new-contacts-tooltip"
                  isSvg
                  descriptionContent="Fresh contacts we have added that match your search criteria are shown here."
                />
              </div>
            </span>
          )}
          <span className="open-search"> </span>
        </div>
        <div className="table-body" ref={tableBodyRef}>
          {savedSearchesToShow.length > 0
            ? savedSearchesToShow.map(savedSearch => {
                const {
                  id,
                  templateName,
                  creationTimeStamp,
                  searchTemplate,
                  memberId,
                  isSharedSearch,
                } = savedSearch;
                return (
                  <div
                    className="table-row"
                    key={id}
                    data-id={id}
                    ref={element => {
                      if (element && elementObserver !== null)
                        elementObserver.observe(element);
                    }}
                  >
                    <div className="table-row-inner-wrap">
                      <div className="saved-search-name">
                        <div title={templateName} className="show-elipsis">
                          {templateName}
                        </div>
                        <SharedListIcon
                          condition={isSharedSearch}
                          iconTitle="Shared search"
                        />
                      </div>
                      <div className="creator">
                        {memberId === userDetail.userInfo.memberId
                          ? 'Me'
                          : userDetail.sharedListAuthorData[memberId]}
                      </div>
                      <div
                        className={`created-on${
                          creationTimeStamp ? '' : ' no-data'
                        }`}
                      >
                        {creationTimeStamp
                          ? Utils.getFormattedDate(creationTimeStamp, true)
                          : '-NA-'}
                      </div>
                      <div className="applied-filters">
                        <RenderAppliedFilters savedSearch={savedSearch} />
                      </div>
                      {Utils.isSavedSearchUpdateEnabled() && (
                        <div
                          className={`email-schedule${
                            savedSearch.frequency &&
                            savedSearch.status === 'ACTIVE'
                              ? ''
                              : ' no-data'
                          }`}
                        >
                          {savedSearch.frequency &&
                          savedSearch.status === 'ACTIVE'
                            ? `${returnCapitalizedWord(
                                savedSearch.frequency,
                              )} on${
                                savedSearch.ordinalDay
                                  ? ` ${returnCapitalizedWord(
                                      savedSearch.ordinalDay,
                                    )}`
                                  : ''
                              } ${returnCapitalizedWord(savedSearch.day)}`
                            : '-NA-'}
                        </div>
                      )}
                      {userDetail.isContactUpdatesEnabled && (
                        <div
                          className={`new-contacts${
                            newContactsCountData.countIds[id] ? '' : ' no-data'
                          }`}
                        >
                          {newContactsCountData.loadingIds.includes(id) ? (
                            <LoadingWrapper show position="absolute" />
                          ) : newContactsCountData.countIds[id] ? (
                            <button
                              className="new-count"
                              type="button"
                              onClick={() => {
                                Utils.mixpanelTrack(
                                  CONSTANTS.MX_SAVED_SEARCH_EVENTS
                                    .NEW_CONTACTS_BUTTON_CLICKED,
                                  {
                                    id,
                                    name: templateName,
                                    newContactsCount:
                                      newContactsCountData.countIds[id],
                                  },
                                );
                                history.push(
                                  `/advanced-search/contact/${id}?tab=2`,
                                );
                              }}
                              data-text={`${
                                newContactsCountData.countIds[id] > 100
                                  ? '99+'
                                  : newContactsCountData.countIds[id]
                              } NEW`}
                            >
                              <RightArrowIcon />
                            </button>
                          ) : (
                            <span>-NA-</span>
                          )}
                        </div>
                      )}
                      <div className="open-search">
                        <div className="wrapper">
                          {searchTemplate !== null && (
                            <div
                              className="open-search-link"
                              onClick={() => {
                                Utils.mixpanelTrack(
                                  CONSTANTS.MX_SAVED_SEARCH_EVENTS
                                    .OPEN_SEARCH_CLICKED,
                                  {
                                    id,
                                    name: templateName,
                                  },
                                );
                                history.push(`/advanced-search/contact/${id}`);
                              }}
                            >
                              Open Search
                            </div>
                          )}
                          {userDetail.userInfo.memberId ===
                          savedSearch.memberId ? (
                            <Dropdown
                              align="end"
                              toggleElement={
                                <div className="menu-wrap">
                                  <TripleDotNavIcon className="menu-icon" />
                                </div>
                              }
                            >
                              <div className="menu-list">
                                <div
                                  className="menu-item"
                                  onClick={() => {
                                    setPopupData({
                                      show: true,
                                      data: savedSearch,
                                    });
                                  }}
                                >
                                  Edit
                                </div>
                                <div
                                  className="menu-item"
                                  onClick={() => deleteSavedSearch(savedSearch)}
                                >
                                  Delete
                                </div>
                                {!savedSearch.isSharedSearch && (
                                  <div
                                    className="menu-item"
                                    onClick={() =>
                                      shareSavedSearch({
                                        ...savedSearch,
                                        isSharedSearch: true,
                                      })
                                    }
                                  >
                                    Share
                                  </div>
                                )}
                                {savedSearch.isSharedSearch && (
                                  <div
                                    className="menu-item"
                                    onClick={() =>
                                      unShareSavedSearch({
                                        ...savedSearch,
                                        isSharedSearch: false,
                                      })
                                    }
                                  >
                                    Unshare
                                  </div>
                                )}
                              </div>
                            </Dropdown>
                          ) : (
                            <div className="menu-placeholder" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            : userDetail.initialLoadedAPIs.contactSavedSearches && (
                <EmptySavedSearches
                  searchTerm={searchTerm}
                  noSavedSearch={userDetail.contactSavedSearches.length === 0}
                  emptySavedSearch={
                    userDetail.contactSavedSearches.length > 0 &&
                    savedSearchesToShow.length === 0 &&
                    searchTerm !== ''
                  }
                />
              )}
        </div>
      </div>
      <Pagination
        totalCount={searchedResults.length}
        rowsPerPage={pagination.count}
        currentPage={pagination.page}
        updateCurrentPage={changePage}
        onRowsPerPageUpdate={changePageLimit}
        rowsPerPageValues={[10, 25, 50].map(data => {
          return { label: data.toString(), value: data };
        })}
      />
    </div>
  );
});

export default SavedSearch;
